// ブラウザ完結版のデータ永続化（IndexedDB, DB=vn_newgames_tool v2）。
// stores:
//   projects      keyPath 'projectId'          … ダッシュボード用の meta 索引（一覧を高速化）
//   projectFiles  keyPath ['projectId','path'] … Node 版 projects/<pid>/ のファイルレイアウトそのまま
//   previewFiles  keyPath ['projectId','path'] … 公開パッケージと同一レイアウト（maps/**, cast/*）
//   assetFiles    keyPath 'path'               … Meshy 生成 GLB 等の共有アセット
//                 （path は 'newgames/<gid>/models/...' の assets/ 相対。gameId を含むため衝突しない。
//                  projectId はプロジェクト削除時の掃除用に保持し、byProject 索引で引く）
// previewFiles / assetFiles は preview-sw.js からも読まれる（プレビュー仮想ホスト。
// DB_VERSION とストア定義は preview-sw.js 側と必ず一致させること＝回帰テストで検証）。

export const DB_NAME = 'vn_newgames_tool';
export const DB_VERSION = 2;
export const STORE_PROJECTS = 'projects';
export const STORE_PROJECT_FILES = 'projectFiles';
export const STORE_PREVIEW_FILES = 'previewFiles';
export const STORE_ASSET_FILES = 'assetFiles';

let dbPromise = null;

export function openDb() {
    if (dbPromise) return dbPromise;
    dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_PROJECTS)) db.createObjectStore(STORE_PROJECTS, { keyPath: 'projectId' });
            if (!db.objectStoreNames.contains(STORE_PROJECT_FILES)) db.createObjectStore(STORE_PROJECT_FILES, { keyPath: ['projectId', 'path'] });
            if (!db.objectStoreNames.contains(STORE_PREVIEW_FILES)) db.createObjectStore(STORE_PREVIEW_FILES, { keyPath: ['projectId', 'path'] });
            if (!db.objectStoreNames.contains(STORE_ASSET_FILES)) {
                db.createObjectStore(STORE_ASSET_FILES, { keyPath: 'path' }).createIndex('byProject', 'projectId');
            }
        };
        req.onsuccess = () => {
            const db = req.result;
            // 別コンテキスト（新しいデプロイのタブ・SW）が上位バージョンを要求したら明け渡す。
            // 保持し続けると新タブの onupgradeneeded が永遠にブロックされる
            db.onversionchange = () => { db.close(); dbPromise = null; };
            resolve(db);
        };
        // 一過性の open 失敗（プライベートモード等）で永続的に壊れないよう、失敗時はキャッシュを消して再試行可能にする
        req.onerror = () => { dbPromise = null; reject(req.error); };
        req.onblocked = () => { dbPromise = null; reject(new Error('IndexedDB open blocked（別タブが旧バージョンを保持しています）')); };
    });
    dbPromise.catch(() => { dbPromise = null; });
    return dbPromise;
}

function tx(db, stores, mode) {
    return db.transaction(stores, mode);
}
function reqP(request) {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}
function txDone(transaction) {
    return new Promise((resolve, reject) => {
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error || new Error('tx aborted'));
    });
}

function projectRange(projectId) {
    // [projectId, path] 全件（array は string より後に整列するため上界に [] を使う）
    return IDBKeyRange.bound([projectId], [projectId, []], false, false);
}

// ── projects（meta 索引）─────────────────────────────
export async function putProjectMeta(meta) {
    const db = await openDb();
    const t = tx(db, STORE_PROJECTS, 'readwrite');
    t.objectStore(STORE_PROJECTS).put(meta);
    await txDone(t);
}
export async function getProjectMeta(projectId) {
    const db = await openDb();
    return reqP(tx(db, STORE_PROJECTS, 'readonly').objectStore(STORE_PROJECTS).get(projectId));
}
export async function listProjectMetas() {
    const db = await openDb();
    return reqP(tx(db, STORE_PROJECTS, 'readonly').objectStore(STORE_PROJECTS).getAll());
}
export async function deleteProjectMeta(projectId) {
    const db = await openDb();
    const t = tx(db, STORE_PROJECTS, 'readwrite');
    t.objectStore(STORE_PROJECTS).delete(projectId);
    await txDone(t);
}

// ── ファイルストア（projectFiles / previewFiles 共通）──────
export async function getAllFiles(store, projectId) {
    const db = await openDb();
    const rows = await reqP(tx(db, store, 'readonly').objectStore(store).getAll(projectRange(projectId)));
    return rows.map((r) => ({ path: r.path, data: r.data }));
}
export async function getFile(store, projectId, path) {
    const db = await openDb();
    const row = await reqP(tx(db, store, 'readonly').objectStore(store).get([projectId, path]));
    return row ? row.data : undefined;
}
export async function putFile(store, projectId, path, data) {
    const db = await openDb();
    const t = tx(db, store, 'readwrite');
    t.objectStore(store).put({ projectId, path, data });
    await txDone(t);
}
/** 一括 put + delete を 1 トランザクションで（flush 用・アトミック）。 */
export async function applyFileChanges(store, projectId, puts = [], deletes = []) {
    const db = await openDb();
    const t = tx(db, store, 'readwrite');
    const os = t.objectStore(store);
    for (const { path, data } of puts) os.put({ projectId, path, data });
    for (const path of deletes) os.delete([projectId, path]);
    await txDone(t);
}
export async function deleteAllFiles(store, projectId) {
    const db = await openDb();
    const t = tx(db, store, 'readwrite');
    t.objectStore(store).delete(projectRange(projectId));
    await txDone(t);
}

// ── assetFiles（Meshy 生成 GLB 等の共有アセット。key は assets/ 相対 path 単独）──────
export async function getAssetFile(path) {
    const db = await openDb();
    const row = await reqP(tx(db, STORE_ASSET_FILES, 'readonly').objectStore(STORE_ASSET_FILES).get(path));
    return row ? row.data : undefined;
}
/** 実在確認のみ（getKey はバイナリ本体を読まないため軽量）。 */
export async function hasAssetFile(path) {
    const db = await openDb();
    const key = await reqP(tx(db, STORE_ASSET_FILES, 'readonly').objectStore(STORE_ASSET_FILES).getKey(path));
    return key !== undefined;
}
/** 接頭辞に一致するアセットキーだけを列挙する（本体バイナリは読まない＝軽量）。 */
export async function listAssetKeysByPrefix(prefix) {
    const db = await openDb();
    const range = IDBKeyRange.bound(prefix, prefix + '\uffff');
    const keys = await reqP(tx(db, STORE_ASSET_FILES, 'readonly').objectStore(STORE_ASSET_FILES).getAllKeys(range));
    return keys.map(String);
}
/** 一括 put + delete を 1 トランザクションで（flush 用・アトミック）。 */
export async function applyAssetChanges(projectId, puts = [], deletes = []) {
    const db = await openDb();
    const t = tx(db, STORE_ASSET_FILES, 'readwrite');
    const os = t.objectStore(STORE_ASSET_FILES);
    for (const { path, data } of puts) os.put({ path, projectId, data });
    for (const path of deletes) os.delete(path);
    await txDone(t);
}
/** プロジェクトに属する生成アセットを列挙する（エクスポート用）。 */
export async function getAssetsForProject(projectId) {
    const db = await openDb();
    const idx = tx(db, STORE_ASSET_FILES, 'readonly').objectStore(STORE_ASSET_FILES).index('byProject');
    const rows = await reqP(idx.getAll(IDBKeyRange.only(projectId)));
    return rows.map((r) => ({ path: r.path, data: r.data }));
}

// ── 容量表示用のサイズ列挙（本体は保持しない＝100MB の GLB を丸ごとメモリへ載せない）──────
/** 1 レコードのバイト数（文字列は UTF-8 換算・バイナリは実バイト数）。 */
function storedByteLength(data) {
    if (data == null) return 0;
    if (typeof data === 'string') return new TextEncoder().encode(data).length;
    if (data instanceof ArrayBuffer) return data.byteLength;
    if (ArrayBuffer.isView(data)) return data.byteLength;
    return 0;
}

/** カーソルで 1 件ずつ path とバイト数だけを取り出す（値は数え終わったら捨てる）。 */
function listSizes(source, range) {
    return new Promise((resolve, reject) => {
        const out = [];
        const req = source.openCursor(range);
        req.onsuccess = () => {
            const cursor = req.result;
            if (!cursor) { resolve(out); return; }
            out.push({ path: cursor.value?.path, bytes: storedByteLength(cursor.value?.data) });
            cursor.continue();
        };
        req.onerror = () => reject(req.error);
    });
}

/** projectFiles / previewFiles の 1 プロジェクト分を {path, bytes} で列挙する。 */
export async function listFileSizes(store, projectId) {
    const db = await openDb();
    return listSizes(tx(db, store, 'readonly').objectStore(store), projectRange(projectId));
}

/** 生成アセット（assetFiles）の 1 プロジェクト分を {path, bytes} で列挙する。 */
export async function listAssetSizes(projectId) {
    const db = await openDb();
    const idx = tx(db, STORE_ASSET_FILES, 'readonly').objectStore(STORE_ASSET_FILES).index('byProject');
    return listSizes(idx, IDBKeyRange.only(projectId));
}

/** プロジェクト削除時の掃除（byProject 索引で該当アセットを全削除）。 */
export async function deleteAssetsForProject(projectId) {
    const db = await openDb();
    const t = tx(db, STORE_ASSET_FILES, 'readwrite');
    const idx = t.objectStore(STORE_ASSET_FILES).index('byProject');
    const req = idx.openKeyCursor(IDBKeyRange.only(projectId));
    req.onsuccess = () => {
        const cur = req.result;
        if (cur) { t.objectStore(STORE_ASSET_FILES).delete(cur.primaryKey); cur.continue(); }
    };
    await txDone(t);
}
